import { PAGE_SLOT_COUNT } from "./slotCount";
import {
  addOrRemovePageBubble,
  clusterCenterCoords,
  fillClusterEmpty,
  fillClusterSelected,
  getEffectiveDateRangeValue,
  getPagesList,
  pageViewDisabled,
  selectedDateRange,
  selectedEndDateRange,
} from "./clusterService";
import { session } from "../services/session";
import { site } from "../services/site";
import * as d3 from "d3";
import { DEFAULT_VALUE } from "../constants/commonConstants";
import storage from "./storage";

import { eventEmitter } from "../eventEmitters/EventEmitters";
import * as playbackModeService from "./playbackModeService";
import { setCachedPages } from "./helper";
import type { SlotChange } from "./slotReorder";

export let data: any;
// 12 real page slots now (was 10) - see clusterService.ts's own
// UNKNOWN_URL_CLUSER_INDEX comment for the full index remap.
const UNKNOWN_URL_CLUSER_INDEX = PAGE_SLOT_COUNT;
export interface PageEntity {
  name: string;
  selected: boolean;
  restricted: boolean;
  firstVisitDate?: Date;
  slotIndex?: number;
}
export const maxRadius = 32;
export interface PageUiEntity extends PageEntity {
  disabled?: boolean;
  currentVisitors?: number;
  overallVisitors?: number;
}
let missingPaths = [];
export const getMissingPathsCount = () => missingPaths.length;
export let clusterIndexToSet: number;

// selectPage/unselectPage each optimistically paint their own box
// synchronously (correct, instant), then in the background: hit the
// backend, overwrite localStorage's full page list with that response, and
// fire "d3componentUpdate" (RightMenu.tsx's listener re-derives `data` and
// repaints every box from whatever's currently in localStorage). Nothing
// previously stopped two of these background chains from overlapping - if a
// user selects/removes pages in quick succession, their backend responses
// aren't guaranteed to resolve in the order the actions were taken, so a
// slower-to-resolve response's stale-by-then localStorage snapshot could
// overwrite a newer one's already-correct state, and the following
// "d3componentUpdate" full rebuild would visibly un-paint/reappear boxes
// that were already right a moment earlier - reported as pages "shuttling
// around and disappearing" on Select/Remove/Clear All. Funneling every
// mutation's background chain through one queue (not the synchronous
// optimistic paint, which stays instant) guarantees they resolve in the
// same order they were started, so a later action can never be undone by an
// earlier one's slower response.
let pageMutationQueue: Promise<any> = Promise.resolve();
const enqueuePageMutation = <T,>(task: () => Promise<T>): Promise<T> => {
  const run = () => task();
  const result = pageMutationQueue.then(run, run);
  pageMutationQueue = result.then(
    () => undefined,
    () => undefined
  );
  return result;
};

export const setPages = async (recods: { siteId: string; pages: any[] }) => {
  /*
   * 🔴 THE CHOKE POINT: REFUSE PAGES THAT BELONG TO ANOTHER SITE.
   *
   * `data` is what the Pages tab renders (d3PagesDataRecord returns it) and
   * what findEmptyIndex computes slot assignments from. EVERY write to it
   * comes through here, from five different callers - a socket refresh, two
   * RightMenu listeners, AnalyticsView's fetch, and clusterService.
   *
   * With multi-site, any one of those can carry the PREVIOUS site's pages: a
   * closure captured before a switch, a cache written by the other site, a
   * fetch that resolved late. The result was the dashboard rendering one
   * site's page list under another site's name - Chris saw second-site.test
   * showing staging.theskydiveapp.com's ten pages, /about appearing in two
   * slot boxes, and selections refusing to advance to the next free slot.
   * All of it downstream of `data` describing the wrong site.
   *
   * Chasing each caller is whack-a-mole: five paths today, and the next one
   * added reintroduces it silently. Guarding HERE makes every stale path
   * inert by construction, whatever its route.
   *
   * 🔴 IT REFUSES LOUDLY, not quietly. A swallowed mismatch would leave the
   * tab showing the last good list while the header says something else -
   * the same wrong-but-plausible state, just harder to find. The warn names
   * both ids so the offending caller is identifiable from a console.
   *
   * Refusing leaves `data` as it was and the correct fetch repopulates it.
   * An empty or stale-but-flagged list is recoverable; a confidently wrong
   * one is not.
   */
  const activeSiteId = storage.get("siteId");

  if (activeSiteId && recods.siteId && String(recods.siteId) !== String(activeSiteId)) {
    // eslint-disable-next-line no-console
    console.warn(
      `[skydive] setPages ignored: pages for site ${recods.siteId} while ${activeSiteId} is open.`
    );

    return;
  }

  const pages = recods.pages;
  const previous = data;
  data = pages?.filter((page) => !page.restricted);
  // 🔴 EVERY route that refreshes the page list lands here (REMOVE, SELECT, a group made, a socket refresh), and the server's pages carry no live occupancy: each page record came back with
  // "Current" at nothing while its visitors were still there, so the next departure took it to -1, -2 ... (reproduced 2026-10-21 with no group at all: press REMOVE, watch /cart reach -4).
  // The old counts are carried over by page name, and whoever owns the bubbles is told to derive the true occupancy again (a page with no earlier record, such as a new group, starts from that).
  previous?.forEach((old) => {
    const fresh = data?.find((p) => p.name === old.name);
    if (fresh && fresh.currentVisitors === undefined && old.currentVisitors !== undefined) fresh.currentVisitors = old.currentVisitors;
  });
  if (pagesReplacedListener) pagesReplacedListener();
  missingPaths = pages.filter((page) => page.restricted);

  setHomeUrlDisabled(data);
  // Single-cluster ("arrow") view mode keeps every box blank/gray
  // (disablePageView) regardless of page data - this ran unconditionally on
  // every data load, including the async page-data fetch that resolves
  // AFTER a fresh mount's initial grid is built, silently overwriting that
  // blank state with real per-page labels moments later. That race is what
  // made single-cluster mode "mix" back into grid view on returning from
  // Settings (a full remount): the blank got reapplied on mount, then this
  // unconditional repopulation undid it right after. Skipping it here while
  // collapsed closes the race at its source instead of chasing timing on
  // the mount side.
  if (!pageViewDisabled) {
    data
      ?.filter((page) => page.selected)
      ?.forEach((page) => fillClusterSelected(page.slotIndex, page.name));
  }
  await getPagesStatistics();
  return data;

};
let pagesReplacedListener: (() => void) | null = null;
/** told whenever the page list was replaced (the live occupancy has to be derived again) */
export const setPagesReplacedListener = (cb: (() => void) | null) => { pagesReplacedListener = cb; };
export const d3PagesDataRecord = () => {
  return data;
};
const setHomeUrlDisabled = (data) => {
  const homePath = data.find((page) => page.name === "/");

  if (homePath) {
    homePath.disabled = true;
  }
};

// Both getPageVisitsCount call sites (here and handleVisitorMovePagesModel
// below) used to always query the LIVE selectedDateRange filter, even while
// in playback - so the Pages drawer's "overallVisitors" column showed
// whatever the live filter happened to be (usually "Today"), completely
// unrelated to the day actually being played back. Scoping to the playback
// day here mirrors RightMenu.tsx's getPlaybackSessionFetchParams for the
// Sessions tab.
export const getPageVisitsDateRange = () => {
  if (playbackModeService.mode === "playback" && playbackModeService.playbackDate) {
    const dayStart = new Date(`${playbackModeService.playbackDate}T00:00:00.000Z`);
    const dayEnd = new Date(dayStart.getTime() + 24 * 60 * 60 * 1000 - 1);
    const now = new Date();
    // Same "today isn't over yet" cap used elsewhere for a same-day
    // playback (see playbackService.getEffectiveDayMs and RightMenu.tsx's
    // getPlaybackSessionFetchParams).
    const effectiveEnd = dayEnd.getTime() > now.getTime() ? now : dayEnd;
    return { startDate: dayStart.toISOString(), endDate: effectiveEnd.toISOString() };
  }
  return {
    endDate: selectedEndDateRange
      ? selectedEndDateRange
      : new Date().toISOString(),
    startDate: selectedDateRange && selectedDateRange.value
      ? new Date(getEffectiveDateRangeValue()).toISOString()
      : new Date(new Date().setHours(0, 0, 0, 0)).toISOString(),
  };
};

export const getPagesStatistics = async () => {
  const pages = await session({
    jsonprc: "2.0",
    method: "getPageVisitsCount",
    params: {
      ...getPageVisitsDateRange(),
      siteId: storage.get("siteId"),
    },
    id: 0,
  });
  return data.forEach((path) => {
    const pathInfo = pages?.data?.result?.find((page) => page._id === path.name);

    path.overallVisitors = pathInfo ? pathInfo?.overallVisitors || 0 : 0;
  });
};

export const selectPage = (record: { pageName: string; siteId: string }) => {
  const { pageName, siteId } = record;
  const selectedPages = data.filter((page) => page.selected);
  if (!isAllowedToSelectPage()) {
    // throw new Error(this.ERROR_MESSAGE.MAX_MAIN_PAGE_VIEW_ERROR);
    return {error:"You have reached the maximum page amount to display for this view according to your subscription"};
  }
  if (selectedPages.length >= DEFAULT_VALUE.UNKNOWN_URL_CLUSER_INDEX) {
    return {error:"You have reached the maximum page amount to display for this view according to your subscription"};

  }
  // Slot 0 is reserved for the homepage - findEmptyIndex already excludes it
  // from its own candidates, but clusterIndexToSet (set when a user clicks a
  // specific box via the "Select Page to Show" overlay) bypasses that and
  // could target 0 directly. Refusing here, not just at findEmptyIndex, is
  // what makes this a real guarantee rather than just "the overlay usually
  // can't reach an occupied box."
  if (clusterIndexToSet === 0 && pageName !== "/") {
    clusterIndexToSet = null;
    return { error: "The first slot is reserved for your homepage." };
  }
  // `clusterIndexToSet ? ... : ...` treated an explicit 0 as falsy (JS
  // truthiness), silently falling through to findEmptyIndex instead of
  // honoring a real click on slot 0 - moot for non-home pages now that
  // slot 0 is guarded above, but a real correctness bug in its own right
  // for the home-reselection case (pageName === "/").
  const indexToSet =
   clusterIndexToSet != null
      ? clusterIndexToSet
      :
    findEmptyIndex(data);
  const selectedPage = data.find((page) => page.name === pageName);


  selectedPage.selected = true;
  selectedPage.slotIndex = indexToSet;

  fillClusterSelected(indexToSet, selectedPage.name);
  clusterIndexToSet = null;

  enqueuePageMutation(() => setPageSelected({ pageName, indexToSet, siteId }));
  addOrRemovePageBubble("addPage",pageName );
};

// Drag-and-drop reorder (docs/DRAG_DROP_SLOTS_SCOPE.md). IN PLACE, so every holder of `data` (the Pages tab, findEmptyIndex) sees the new
// slots. A change is matched on the page's name AND the slot it is leaving, so a swap is safe whatever order the pages are visited in, and
// a page that is no longer where the plan assumed (a second reorder got there first) is left alone.
export const applySlotChangesInPlace = (changes: SlotChange[]) => {
  (data || []).forEach((page: any) => {
    if (!page?.selected) {
      return;
    }

    const change = changes.find((c) => c.pageName === page.name && c.fromSlot === page.slotIndex);

    if (change) {
      page.slotIndex = change.toSlot;
    }
  });
};

// One entry per page name, preferring the SELECTED one (pages can carry a stale unselected duplicate).
const dedupePagesByName = (pages: any[]) => {
  const byName = new Map<string, any>();

  pages.forEach((page) => {
    const kept = byName.get(page.name);

    if (!kept || (!kept.selected && page.selected)) {
      byName.set(page.name, page);
    }
  });

  return Array.from(byName.values());
};

export type SlotSaveOutcome = { status: "ok" | "noop" | "conflict" | "error" };

// Saves a reorder through the SAME serial queue as select/unselect, so overlapping responses cannot overwrite each other.
//   'ok' / 'noop'  the server has it.
//   'conflict'     the server did NOT apply it (a page was not where we said): its pages are adopted here, and the caller redraws from them.
//   'error'        nothing is known about the server: the caller puts the change back.
export const saveSlotReorder = (changes: SlotChange[], siteId: string): Promise<SlotSaveOutcome> =>
  enqueuePageMutation(async (): Promise<SlotSaveOutcome> => {
    try {
      const response = await site({
        id: 0,
        jsonprc: "2.0",
        method: "reorderPages",
        params: { siteId, moves: changes },
      });
      const result = response?.data?.result;

      if (!result || typeof result.status !== "string") {
        return { status: "error" };
      }

      const pages = Array.isArray(result.pages) ? result.pages : null;

      if (pages) {
        setCachedPages(dedupePagesByName(pages));
      }

      if (result.status === "conflict" && pages) {
        await setPages({ siteId, pages });
      }

      getPagesList();
      eventEmitter.emit("d3componentUpdate");

      return { status: result.status === "conflict" ? "conflict" : result.status === "noop" ? "noop" : "ok" };
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("[skydive] reorderPages failed:", error);

      return { status: "error" };
    }
  });

export const clearSelectedPages = ()=> {
  return new Promise(async (resolve) => {
const filterData = data.filter(page => page.selected && !page.disabled);
data.filter(page => page.selected && !page.disabled);
if(filterData.length ){
  for(let i=0; i< filterData.length; i++){
    await unselectPage({pageName:filterData[i].name,siteId:storage.get("siteId")});
    if(i === (filterData.length-1) ){
      resolve("ok");
    }
  }
  eventEmitter.emit("d3componentUpdate");
} else{
  resolve("ok");
}
  });
 
};
const setPageSelected = async (record: {
  pageName: string;
  indexToSet: number;
  siteId: string;
}) => {
  const param = {
    id: 0,
    jsonprc: "2.0",
    method: "selectPage",
    params: {
      pageName: record.pageName,
      siteId: record.siteId,
      slotIndex: record.indexToSet,
    },
  };
  const updateSelectedPage = await site(param);
  const uniquePages = updateSelectedPage?.data?.result?.pages?.filter((obj, index) => {
    return index === updateSelectedPage?.data?.result?.pages.findIndex(o => obj.name === o.name);
});
  // A failed/short-circuited response above must never overwrite the
  // stored pages with an invalid value - see storage.ts's getJSON comment.
  if (uniquePages) {
    setCachedPages(uniquePages);
  }

  getPagesList();
  eventEmitter.emit("d3componentUpdate");
  // Same unguarded `.pages` as setPageUnselected had - see its note. This one
  // is not inside a Promise executor so it would surface as a rejection
  // rather than a hang, but it is the same latent crash on an error response.
  return updateSelectedPage?.data?.result?.pages ?? [];


};

// Slot 0 is reserved for the homepage ("/") - always static, top-left,
// never reassignable to any other page. Excluding it from this candidate
// list entirely (not just relying on it usually being occupied) means it
// stays reserved even if home's slot were ever momentarily unassigned (a
// redraw race, a stale backend sync) - nothing else can claim it.
const findEmptyIndex = (pages: PageUiEntity[]) => {
  // Was a hardcoded [1, 2, ..., 9] - never included the 2 new slots (indices
  // 10-11) added alongside the 12-slot grid layout, so once the original 9
  // non-home slots were full, this silently returned undefined instead of
  // finding the new ones. A page selected under that undefined slotIndex
  // got marked "selected" in the panel but had nowhere real to render -
  // resolveRealtimeClusterCoords has no page.slotIndex to match against, so
  // nothing showed up on the dashboard. Derived from
  // UNKNOWN_URL_CLUSER_INDEX now instead of a fixed list, so this can't
  // silently drift out of sync with the slot count again.
  const clusterIndexes = Array.from(
    { length: UNKNOWN_URL_CLUSER_INDEX - 1 },
    (_, i) => i + 1
  );

  const takenClusterIndexes = pages
    .filter((page) => page.selected)
    .map((page) => page.slotIndex);

  return clusterIndexes.find((index) => !takenClusterIndexes.includes(index));
};

export const unselectPage = async(record: { pageName: string; siteId: string }) => {
  return new Promise(async (resolve) => {
    try {
      const page = data.find((page) => page.name === record.pageName);

      /*
       * 🔴 THE PERMANENT SPINNER (fixed 2026-09-28).
       *
       * This was `page.selected = false` with no guard, while the line above
       * it already used `page?.slotIndex` - so the author knew `page` could
       * be undefined and only protected one of the two.
       *
       * When it threw, the throw happened inside `new Promise(async ...)`.
       * A rejection in an async executor is SWALLOWED: the promise never
       * resolves and never rejects. RightMenu's `await unselectPage(...)`
       * therefore hung forever and setPageSelectLoading(false) was never
       * reached - the REMOVE button span forever with no error anywhere.
       *
       * Same class as the missing `resolve(undefined)` in drawUserBubble that
       * CLAUDE.md already records: anything inside this wrapper fails
       * silently, so every path must settle the promise explicitly.
       *
       * `data` can legitimately not contain the page: it is filtered to
       * non-restricted pages, and it is replaced wholesale whenever the site
       * changes. Removing something that is not there is a no-op, not an
       * error - but it must be a LOUD no-op, or the next person debugging a
       * missing row has nothing to go on.
       */
      if (!page) {
        // eslint-disable-next-line no-console
        console.warn(
          `[skydive] unselectPage: "${record.pageName}" is not in the current page list - nothing to remove.`
        );
        resolve("not-found");

        return;
      }

      fillClusterEmpty(page.slotIndex);

      page.selected = false;
      page.slotIndex = null;

      await enqueuePageMutation(() =>
        setPageUnselected({ pageName: record.pageName, siteId: record.siteId })
      );
      await addOrRemovePageBubble("removePage", record.pageName);

      resolve("ok");
    } catch (error) {
      // Settle, always. A caller awaiting this must never be left hanging -
      // that is the whole bug above.
      // eslint-disable-next-line no-console
      console.error(`[skydive] unselectPage failed for "${record.pageName}":`, error);
      resolve("error");
    }
  });
};
const setPageUnselected = async (record: {
  pageName: string;
  siteId: string;
}) => {
  return new Promise(async(resolve, reject) => {

    const param = {
      id: 0,
      jsonprc: "2.0",
      method: "unselectPage",
      params: {
        pageName: record.pageName,
        siteId: record.siteId,
      },
    };
    /*
     * 🔴 THE PERMANENT SPINNER ON REMOVE (fixed 2026-09-28).
     *
     * This ended with:
     *     resolve(updateSelectedPage?.data?.result.pages);
     *                                       ^^^^^^^ chaining STOPS here
     *
     * When the RPC returns anything but a success - an error envelope, a 403
     * from checkResourceOwnership, a page that does not belong to this site -
     * `result` is undefined and `.pages` THROWS. The throw happens inside
     * `new Promise(async ...)`, where a rejection is SWALLOWED: the promise
     * never resolves and never rejects.
     *
     * RightMenu awaits this, so setPageSelectLoading(false) was never
     * reached and the REMOVE button span forever, with no error shown and
     * nothing in the console.
     *
     * Third instance of this exact pattern in this codebase - CLAUDE.md
     * already records drawUserBubble's missing resolve(). ANY work inside an
     * async Promise executor must settle on EVERY path, including the ones
     * that throw.
     */
    try {
      const updateSelectedPage = await site(param);
      const pages = updateSelectedPage?.data?.result?.pages;

      const uniquePages = pages?.filter((obj, index) => {
        return index === pages.findIndex(o => obj.name === o.name);
      });

      if (uniquePages) {
        setCachedPages(uniquePages);
      }

      getPagesList();

      if (!pages) {
        // A refusal is a real outcome, not a crash - say so and settle.
        // eslint-disable-next-line no-console
        console.warn(
          `[skydive] unselectPage RPC returned no pages for "${record.pageName}" on site ${record.siteId}:`,
          updateSelectedPage?.data?.error ?? updateSelectedPage?.error ?? updateSelectedPage
        );
      }

      resolve(pages ?? []);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(`[skydive] unselectPage RPC failed for "${record.pageName}":`, error);
      resolve([]);
    }
  });

};


export const getClusterNumber = (path) => {
  const nodePage = data.find((page) => page.name === path && page.selected);

  return nodePage ? nodePage.slotIndex : UNKNOWN_URL_CLUSER_INDEX;
};

export const createCollidesHandler = (bubblesData1) => {
  const bubblesData = JSON.parse(JSON.stringify(bubblesData1));
  const clusterPadding = 0;
  const alpha = 0.5;
  const padding = 1;

  const quadtree = d3
    .quadtree()
    .x((d: any) => d.vx) // Replace 'x' with the key that stores the x-coordinate in your data objects
    .y((d: any) => d.vy) // Replace 'y' with the key that stores the y-coordinate in your data objects
    .addAll(bubblesData);

  return  (d) => {
    const r = d.radius + maxRadius + Math.max(padding, maxRadius);
    const nx1 = d.x - r;
    const nx2 = d.x + r;
    const ny1 = d.y - r;
    const ny2 = d.y + r;
    quadtree.visit((quad: any, x1, y1, x2, y2) => {

      if (quad.data && quad.data !== d) {
        let x = d.x - quad.data.x;
        let y = d.y - quad.data.y;

        let l = Math.sqrt(x * x + y * y);
        const r =
          d.radius +
          quad.data.radius +
          (d.cluster === quad.data.cluster ? padding : clusterPadding);


        if (l < r) {
          l = ((l - r) / l) * alpha;

          d.x -= x *= l;
          d.y -= y *= l;
          quad.data.x += x;
          quad.data.y += y;

        }
      }


      return x1 > nx2 || x2 < nx1 || y1 > ny2 || y2 < ny1;
    });
  };
};

export const createGravityHandler = (alpha) => {
 
  return (d1) => {
    const d = JSON.parse(JSON.stringify(d1));
    const nodeClusterName = d.currentUrlName;

    const centerIndex = DEFAULT_VALUE.DEFAULT_CLUSTER_CENTER[nodeClusterName]
      ? DEFAULT_VALUE.DEFAULT_CLUSTER_CENTER[nodeClusterName].centerIndex
      : pageViewDisabled
      ? DEFAULT_VALUE.ALL_CLUSTER_INDEX
      : getClusterNumber(d.currentUrlName);

    d.x += (clusterCenterCoords[centerIndex].exteraX - d.x) * (alpha + 0.5);
    d.y += (clusterCenterCoords[centerIndex].exteraY - d.y) * (alpha + 0.5);
  };
};
// `countPageview` MUST be false for any caller that is re-drawing, re-syncing
// or reconstructing a visitor who was ALREADY counted - a repaint is not a
// navigation. Passing true from those paths is what inflated the Pages tab's
// pageview total to 28k against a real backend figure of 17k after a tab was
// left open overnight: every reconnect replays every onsite session
// (flushActiveSessions) and every reconcile/redraw walked every existing
// bubble, each one landing here and incrementing as though someone had just
// navigated. Only a genuine arrival or a genuine track-url move may pass true.
export const handleVisitorMovePagesModel = async(previousPageName, newPageName, countPageview = true) => {
  // Both live and playback can have many pageview actions due in rapid
  // succession (playback's tick/burst loop, or just continuous real
  // traffic) - each one awaiting a real network round trip here used to
  // serialize the whole thing behind however fast the backend could answer,
  // one request at a time. Confirmed live: under sustained test traffic this
  // fired a full getPageVisitsCount round trip (all pages, every time) on
  // every single arrival/pageview/departure, over 100 requests in a few
  // seconds - the actual cause of a reported bubble-movement "stutter"
  // (the RPC/JSON-parse/state-update work was blocking the main thread long
  // enough to delay the rAF tick that drives bubble position, which reads as
  // stutter right as a CSS-eased move retargets). Originally only fixed for
  // playback (see git history) by incrementing overallVisitors locally
  // instead of re-fetching, since it's already refreshed at full accuracy on
  // entering/switching playback (RightMenu.tsx's refreshPagesStatsForMode).
  // Live mode gets the exact same treatment now, for the same reason -
  // getPagesStatistics()/refreshPagesStatsForMode already periodically
  // resync it, so per-navigation bookkeeping here can stay purely local.
  const newPage = data.find(
    (page) => !page.restricted && page.name === newPageName
  );

  const previousPage = data.find(
    (page) => !page.restricted && page.name === previousPageName
  );

  if (previousPage) {
    previousPage.currentVisitors--;
  }

  if (newPage) {
    if (countPageview) {
      newPage.overallVisitors = Number(newPage.overallVisitors)
        ? newPage.overallVisitors + 1
        : 1;
      newPage.currentVisitors = Number(newPage.currentVisitors)
      ? newPage.currentVisitors + 1
      : 1;
    }

    return;
  }
  if (newPageName !== null) {
    const urlsToTrack =Number(storage.get("urlsToTrack"));

  const limitNotReached = urlsToTrack > 0 && data.length < urlsToTrack;
  if (limitNotReached || urlsToTrack === -1) {

    return data.push({
      name: newPageName,
      selected: false,
      restricted: false,
      // Seeded at 0 for resync/redraw callers - the page still needs to EXIST
      // in the list (otherwise it silently disappears from the Pages tab), but
      // a repaint must not manufacture a visit. syncCurrentVisitorsFromPageNames
      // sets real occupancy immediately afterwards on those paths.
      currentVisitors: countPageview ? 1 : 0,
      overallVisitors: countPageview ? 1 : 0,
    });
  }


    const nonTrackedPath = missingPaths.find(
      (path) => path.name === newPageName
    );

    if (!nonTrackedPath) {
      missingPaths.push({
        name: newPageName,
      });
    }
  }

};
// Re-derives each page's LIVE OCCUPANCY (currentVisitors) from the actual set
// of on-screen visitor bubbles, rather than accumulating +1/-1 deltas.
//
// The distinction matters and is the heart of the 28k-vs-17k bug: overallVisitors
// is a CUMULATIVE total (a real pageview happened, increment forever), but
// currentVisitors is a STATE (how many people are on this page right now).
// A state must never be accumulated across resyncs - run the same reconcile
// twice and an incremental counter doubles, while a derived one is unchanged.
// This function is idempotent by construction: call it any number of times and
// the answer is whatever the bubbles actually say.
//
// Pass the page name of every currently-rendered real-time bubble.
export const syncCurrentVisitorsFromPageNames = (pageNames: (string | null | undefined)[]) => {
  const counts = new Map<string, number>();
  for (const name of pageNames) {
    if (!name) {
      continue;
    }
    counts.set(name, (counts.get(name) || 0) + 1);
  }
  data.forEach((page) => {
    if (!page.restricted) {
      page.currentVisitors = counts.get(page.name) || 0;
    }
  });
};

export const isAllowedToSelectPage=() =>{
  const totalMainPageViews = storage.get("mainViewSlots");

  const selectedPages =data.filter(page => page.selected);
  return selectedPages.length < totalMainPageViews;
};

export const setClusterIndexToSet =(data)=>{
  clusterIndexToSet = data;
};
